/**
 * 语音意图路由器
 *
 * 作用：把用户语音输入的文本鲁棒地匹配到对应的业务面板。
 *
 * 相比原来的 _tryKeywordMatch（单一关键词 includes 匹配）：
 * 1. 覆盖所有已注册面板（监控、调度、供水、建造、运维、应急、工程、通知、
 *    洪水预演、自定义调度、知识问答、施工安全、调度问答、供需平衡、
 *    故事线导览、淹没分析、多方案对比等）；
 * 2. 每个面板配有同义词、别名、口语化表达；
 * 3. 支持编辑距离 ≤1 的模糊匹配，容忍语音识别错别字；
 * 4. 支持"打开/进入/查看/跳转"等动词前缀剥离后再匹配；
 * 5. 优先级评分：精确命中 > 包含主词 > 包含同义词 > 模糊匹配。
 */

import { getActiveScenarioInfo } from '@/services/emergencyMapData.js'

/* ---------- 编辑距离（Levenshtein）---------- */
function levenshtein(a, b) {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  const dp = Array.from({ length: m + 1 }, (_, i) => [i])
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost)
    }
  }
  return dp[m][n]
}

/* 模糊匹配：编辑距离 ≤1 且词长 ≥2 */
function fuzzyMatch(text, keyword) {
  if (keyword.length < 2) return false
  if (text.includes(keyword)) return true
  // 滑动窗口比较
  const kl = keyword.length
  for (let i = 0; i <= text.length - kl; i++) {
    const seg = text.slice(i, i + kl)
    if (levenshtein(seg, keyword) <= 1) return true
  }
  // 如果关键词比文本长，整体比较
  if (text.length >= 2 && text.length <= kl + 1) {
    return levenshtein(text, keyword) <= 1
  }
  return false
}

/* ---------- 面板意图词典 ----------
 * panelKey  : panelStore.open(key) 用的 key
 * method    : $emit('command', method) 给 App.vue 用的事件名（兼容旧逻辑）
 * reply     : 打开面板后的语音回复
 * primary   : 主关键词（命中得分最高）
 * aliases   : 同义词 / 别名 / 口语表达
 * exclude   : 排除词——如果文本包含这些词，则不匹配此面板（避免歧义）
 */
const PANEL_INTENTS = [
  {
    panelKey: 'monitoring',
    method: 'showComprehensiveMonitoring',
    reply: '已打开综合监控大屏。当前监测古贤水库水位617.2米，入库流量1203立方米每秒，出库流量940立方米每秒，各项指标正常。你可以问我具体站点的实时数据。',
    primary: ['监控', '监测', '综合监控', '实时监控'],
    aliases: ['大屏', '看板', '水情', '雨情', '水位', '流量监测', '水质监测', '实时数据', '监控大屏', '监控中心'],
    exclude: ['洪水', '预演', '演进', '调度', '生态流量', '设计', '规范', '防渗'],
  },
  {
    panelKey: 'scheduling',
    method: 'showWaterDistributionLines',
    reply: '已进入运行调度模块。当前运行方案为均衡调度，下泄流量940立方米每秒。你可以查看调度任务列表或切换调度方案。',
    primary: ['调度', '运行调度', '配水', '调度任务', '调度方案'],
    aliases: ['运行管理', '调度管理', '任务调度', '下泄调度', '配水调度', '调度中心'],
    exclude: ['洪水', '预演', '供需', '问答', '知识', '咨询'],
  },
  {
    panelKey: 'waterSupply',
    method: 'showWaterSupplyScale',
    reply: '已打开供水规模面板。古贤受水区覆盖韩城、宜川等6个分区，设计年供水量约23.76亿立方米。你可以对比各分区供水方案。',
    primary: ['供水规模', '供水方案', '供水对比'],
    aliases: ['供水', '供水数据', '规模对比', '供水分析', '供水指标'],
    exclude: ['供需', '缺口', '调度'],
  },
  {
    panelKey: 'supplyDemand',
    method: null,
    reply: '已打开供需平衡分析。当前整体供需保障率约85%，其中韩城片区略有缺口。你可以查看分区缺口详情或调节供水方案。',
    primary: ['供需平衡', '供需分析', '供需缺口'],
    aliases: ['供需', '缺口', '需水', '平衡分析', '供水需求', '供需保障', '缺水'],
    exclude: [],
  },
  {
    panelKey: 'construction',
    method: 'handleSmartConstruction',
    reply: '已进入智能建造模块。当前在建标段12个，整体进度72%，BIM模型已同步。你可以查看施工进度或质量安全预警。',
    primary: ['智能建造', '建造', '施工', 'BIM'],
    aliases: ['施工管理', '建造管理', '智能施工', '建造模块', '工地', '施工安全'],
    exclude: ['安全问答', '知识', '防渗', '设计规范', '重力坝', '安全管理', '安全要求', '安全知识'],
  },
  {
    panelKey: 'maintenance',
    method: 'handleMaintenance',
    reply: '已进入运维管理模块。当前待处理工单5条，其中紧急1条。你可以查看工单列表或设备巡检记录。',
    primary: ['运维', '运维管理', '运行维护'],
    aliases: ['维护', '巡检', '工单', '设备维护', '运维工单', '值班', '设备管理', '设施维护'],
    exclude: [],
  },
  {
    panelKey: 'emergency',
    method: 'handleEmergency',
    reply: () => {
      const info = getActiveScenarioInfo()
      return `已进入应急管理模块。当前防汛应急响应三级，${info.name}预案已就绪。你可以查看应急物资、队伍部署或联动方案。`
    },
    primary: ['应急', '应急管理', '应急预案', '应急联动'],
    aliases: ['预案', '防汛', '抢险', '风险研判', '应急物资', '应急队伍', '避险', '预警预案', '应急处置'],
    exclude: ['洪水预演', '洪水演进', '防渗', '设计', '规范', '重力坝', '坝基', '导流', '消能', '边坡'],
  },
  {
    panelKey: 'project',
    method: 'handleProjectManagement',
    reply: '已进入工程管理模块。古贤枢纽主体工程进度80%，大坝、电站、泄洪建筑物三大标段推进中。你可以查看里程碑节点或质量安全指标。',
    primary: ['工程管理', '工程进度', '工程项目'],
    aliases: ['工程', '进度', '里程碑', '施工单位', '质量安全', '巡检', '工程概况', '工程总览', '工程态势'],
    exclude: ['工程参数', '工程指标', '工程设计', '工程规范', '知识问答', '安全问答'],
  },
  {
    panelKey: 'notice',
    method: 'handleOperationDispatch',
    reply: '已打开通知公告面板。当前有3条未读通知，包括调度指令和防汛预警。你可以查看详情或标记已读。',
    primary: ['通知公告', '公告', '通知'],
    aliases: ['通告', '公告管理', '通知管理', '公告通知', '运营调度', '业务通知'],
    exclude: [],
  },
  {
    panelKey: 'floodSim',
    method: null,
    reply: '已打开洪水预演面板。当前预演方案为20年一遇洪水、均衡调度，龙门断面洪峰已推演完成。你可以切换频率或调整下泄方案。',
    primary: ['洪水预演', '洪水演进', '淹没分析', '洪水模拟'],
    aliases: ['预演', '演进', '淹没', '洪演', '洪水', '溃坝', '淹没仿真', '洪水仿真', '防洪推演'],
    exclude: ['防渗', '设计', '规范', '重力坝', '坝基', '消能', '导流'],
  },
  {
    panelKey: 'custom',
    method: null,
    reply: '已打开自定义调度面板。你可以设置保守、均衡或泄洪优先方案，调整起始水位和洪峰参数后启动预演。',
    primary: ['自定义调度', '调度参数', '补偿调度'],
    aliases: ['固定下泄', '补偿调度', '自定义参数', '调度预演参数'],
    exclude: [],
  },
  {
    panelKey: 'mulSchemes',
    method: null,
    reply: '已打开来水预测方案面板。当前展示多方案预测与精度评定：实测、P50预测方案、P90偏丰方案和P10偏枯方案的流量过程线对比，精度评定为甲级。你可以查看峰现时间、洪峰流量和重现期等指标。',
    primary: ['来水预测方案', '预测方案', '多方案预测', '多方案对比', '方案对比', '预报对比', '精度评定'],
    aliases: ['方案比选', '预报对比', '多方案', '方案比较', '来水预报', '来水预测', '枯季径流', '径流预测', '流量过程线', '预报方案', '预报精度', '洪峰流量', '峰现时间'],
    exclude: [],
  },
  {
    panelKey: 'knowledgeQA',
    method: null,
    reply: '已打开工程设计知识问答。我可以回答设计规范、抗滑稳定、防渗、泄洪消能等问题。请直接提问。',
    primary: ['知识问答', '工程设计知识', '设计知识'],
    aliases: ['知识', '专家', 'AI交互', 'AI助手', '智能助手', '知识库', '设计规范', '设计标准', '防渗', '重力坝', '坝基', '导流', '泄洪', '消能', '边坡', '抗滑', '安全系数', '计算书', '规范符合性', '设计审查', '生态流量'],
    exclude: ['施工安全', '安全问答', '调度问答'],
  },
  {
    panelKey: 'safetyQA',
    method: null,
    reply: '已打开施工安全知识问答。涵盖安全规范、应急预案、隐患排查等内容。请直接提问。',
    primary: ['施工安全问答', '安全知识问答', '安全问答'],
    aliases: ['施工安全', '安全知识', '安全教育', '安全管理知识'],
    exclude: ['调度问答'],
  },
  {
    panelKey: 'schedulingQA',
    method: null,
    reply: '已打开运行调度知识问答。涵盖调度规则、配水方案、应急调度等内容。请直接提问。',
    primary: ['调度问答', '调度知识问答'],
    aliases: ['调度知识', '调度咨询', '调度问题'],
    exclude: [],
  },
  {
    panelKey: 'storyline',
    method: null,
    reply: '已打开演示导览。',
    primary: ['故事线', '演示导览', '导览'],
    aliases: ['故事', '演示', '导览', '导航', '讲解', '介绍', '总体介绍'],
    exclude: [],
  },
  {
    panelKey: 'reservoirInfo',
    method: null,
    reply: '已打开水库工程参数信息。',
    primary: ['水库参数', '水库信息', '工程参数', '工程指标'],
    aliases: ['库容', '特征水位', '正常蓄水位', '死水位', '汛限水位', '装机容量', '水库详情', '水库属性'],
    exclude: [],
  },
  /* ===== C 类工程信息面板语音路由 ===== */
  {
    panelKey: 'irrigation',
    method: null,
    reply: '已打开灌区工程信息面板。当前查看灌区基本信息和供需水方案，可切换查看应急方案和实时监测曲线。',
    primary: ['灌区信息', '灌区参数', '灌区工程'],
    aliases: ['灌区', '灌溉', '灌溉信息', '灌溉区', '灌区详情', '灌区属性'],
    exclude: ['供需平衡', '供需分析'],
  },
  {
    panelKey: 'pumping',
    method: null,
    reply: '已打开泵站工程信息面板。当前查看泵站设计流量、装机功率等参数及实时运行曲线。',
    primary: ['泵站信息', '泵站参数', '泵站工程'],
    aliases: ['泵站', '抽水泵站', '提水泵站', '泵站详情', '水泵站', '泵'],
    exclude: [],
  },
  {
    panelKey: 'pool',
    method: null,
    reply: '已打开水池工程信息面板。当前查看调蓄水池的特征水位、库容等参数及实时监测曲线。',
    primary: ['水池信息', '水池参数', '水池工程'],
    aliases: ['水池', '调蓄水池', '蓄水池', '水池详情', '水池属性'],
    exclude: [],
  },
  {
    panelKey: 'protection',
    method: null,
    reply: '已打开保护工程信息面板。当前查看保护工程的参数和运行状态。',
    primary: ['保护工程', '保护信息', '防护工程'],
    aliases: ['保护', '防护', '保护设施', '防护设施'],
    exclude: ['应急', '预案'],
  },
  {
    panelKey: 'industrial',
    method: null,
    reply: '已打开工业园区信息面板。当前查看工业园区的供水信息和工程参数。',
    primary: ['工业园', '工业园区信息', '工业园信息'],
    aliases: ['工业园区', '工业区', '工业信息', '园区信息'],
    exclude: [],
  },
  {
    panelKey: 'hightTrunk',
    method: null,
    reply: '已打开干管工程信息面板。当前查看供水干管的设计参数和运行状态。',
    primary: ['干管信息', '干管参数', '干管工程'],
    aliases: ['干管', '主管', '干线', '干管详情', '供水主管'],
    exclude: [],
  },
  {
    panelKey: 'branchTrunk',
    method: null,
    reply: '已打开支管工程信息面板。当前查看供水支管的设计参数和运行状态。',
    primary: ['支管信息', '支管参数', '支管工程'],
    aliases: ['支管', '支线', '支管详情', '供水支管'],
    exclude: [],
  },
  {
    panelKey: 'reservoirCtrl',
    method: null,
    reply: '已打开设施定位控制面板。你可以在地图上定位水库、泵站和关键设施，查看实时状态。',
    primary: ['设施定位', '设施控制', '定位控制'],
    aliases: ['定位', '设施', '飞行定位', '定位水库', '定位泵站', '定位设施'],
    exclude: [],
  },
]

/* ---------- 动词前缀剥离 ---------- */
const VERB_PREFIXES = ['打开', '进入', '查看', '看一?下?', '显示', '切换到', '跳转到', '我要看', '帮我(打开|进入|查看)?', '进入一下', '去看', '看一下']

function stripVerbPrefix(text) {
  let stripped = text
  for (const v of VERB_PREFIXES) {
    const re = new RegExp('^' + v, 'i')
    if (re.test(stripped)) {
      stripped = stripped.replace(re, '').trim()
      break
    }
  }
  return stripped
}

/**
 * 路由语音意图到面板。
 *
 * @param {string} rawText 用户输入文本
 * @returns {{ panelKey: string, method: string|null, reply: string } | null}
 */
export function routePanelIntent(rawText) {
  if (!rawText) return null
  const text = String(rawText).trim()

  // 特殊：返回主界面 / 退出 / 关闭
  // 支持单独说"返回"、"关闭"、"回去"等简短指令
  if (/(返回|回到|退出|关闭|回去|回退).*(主界面|首页|主页|大屏|上一步|上一页)|^(返回|返回主界面|返回首页|回首页|回主页|关闭|关闭窗口|关闭面板|退出|退出系统|回去|上一页|上一步)$/.test(text)) {
    return { panelKey: '__exit__', method: 'exitSystem', reply: '已关闭当前面板并返回主界面。你可以继续让我打开综合监控、洪水演进、供水分析或应急管理。' }
  }

  const stripped = stripVerbPrefix(text)

  let bestMatch = null
  let bestScore = 0

  for (const intent of PANEL_INTENTS) {
    // 排除词检查
    if (intent.exclude && intent.exclude.some(ex => text.includes(ex))) continue

    let score = 0

    // 主关键词精确包含：得分 100
    for (const kw of intent.primary) {
      if (text.includes(kw)) { score = Math.max(score, 100); break }
    }
    // 动词剥离后主关键词命中：得分 95
    if (score < 100) {
      for (const kw of intent.primary) {
        if (stripped.includes(kw)) { score = Math.max(score, 95); break }
      }
    }
    // 别名包含：得分 80
    if (score < 80) {
      for (const kw of intent.aliases) {
        if (text.includes(kw)) { score = Math.max(score, 80); break }
      }
    }
    // 模糊匹配主关键词：得分 60
    if (score < 60) {
      for (const kw of intent.primary) {
        if (fuzzyMatch(stripped, kw)) { score = Math.max(score, 60); break }
      }
    }
    // 模糊匹配别名：得分 50
    if (score < 50) {
      for (const kw of intent.aliases) {
        if (fuzzyMatch(stripped, kw)) { score = Math.max(score, 50); break }
      }
    }

    if (score > bestScore) {
      bestScore = score
      bestMatch = intent
    }
  }

  // 阈值：至少 50 分才认为是有效意图
  if (bestMatch && bestScore >= 50) {
    return {
      panelKey: bestMatch.panelKey,
      method: bestMatch.method,
      reply: typeof bestMatch.reply === 'function' ? bestMatch.reply() : bestMatch.reply,
    }
  }

  return null
}

export default routePanelIntent
